import React, { useMemo, useRef } from 'react';
import Plot from 'react-plotly.js';
import {
  adaptiveSamplingTheme,
  basePlotlyLayout,
  paletteToColorscale,
} from './theme';
import { FullscreenButton } from './FullscreenButton';
import { useIsFullscreen } from './useIsFullscreen';

export type HeatmapPanelProps = {
  x: number[];
  y: number[];
  z: number[][];
  uncertainty?: number[][];
  xLabel: string;
  yLabel: string;
  zLabel: string;
  /**
   * When set to `'ridge'`, a contour line is drawn along the max-z ridge in
   * the adaptive-sampling pink highlight. Default `'none'`.
   */
  highlight?: 'ridge' | 'none';
  onClick?: (x: number, y: number) => void;
  /**
   * Hover in data coordinates (null on unhover). Optional + additive: feeds
   * the shared Study-Cursor crosshair (FXC-9009) without changing existing
   * callers.
   */
  onHover?: (point: { x: number; y: number } | null) => void;
  /** Rendered inside the chart container (e.g. crosshair / point overlays). */
  overlay?: React.ReactNode;
  /** Fill the parent's height instead of the fixed 420px letterbox (hero lanes). */
  fillHeight?: boolean;
  /**
   * Optional plot chrome override (FXC-9009 §9.12): callers embedded in the
   * V5 shell resolve their design tokens (e.g. via useHighliftTheme) and pass
   * them here so paper/plot backgrounds, fonts, grid and hover chrome follow
   * the host theme. Omitted → the adaptive-sampling dark theme (the mock
   * views keep their look). The plasma colorscale is kept either way.
   */
  plotAppearance?: {
    paperBg?: string;
    plotBg?: string;
    text?: string;
    muted?: string;
    grid?: string;
    hoverBg?: string;
    hoverBorder?: string;
  };
  fullscreen?: boolean;
};

/**
 * For every column of x, find the y that maximises z. Returns a ridge polyline
 * suitable for an overlay contour / scatter.
 */
function computeRidge(
  x: number[],
  y: number[],
  z: number[][],
): { x: number[]; y: number[] } {
  const rx: number[] = [];
  const ry: number[] = [];
  if (!z.length || !x.length || !y.length) return { x: rx, y: ry };
  for (let i = 0; i < x.length; i++) {
    let bestJ = 0;
    let bestZ = -Infinity;
    for (let j = 0; j < y.length; j++) {
      const row = z[j];
      if (!row) continue;
      const v = row[i];
      if (v === undefined || v === null || Number.isNaN(v)) continue;
      if (v > bestZ) {
        bestZ = v;
        bestJ = j;
      }
    }
    const xi = x[i];
    const yj = y[bestJ];
    if (xi !== undefined && yj !== undefined && Number.isFinite(bestZ)) {
      rx.push(xi);
      ry.push(yj);
    }
  }
  return { x: rx, y: ry };
}

const HeatmapPanelImpl: React.FC<HeatmapPanelProps> = ({
  x,
  y,
  z,
  uncertainty,
  xLabel,
  yLabel,
  zLabel,
  highlight = 'none',
  onClick,
  onHover,
  overlay,
  fillHeight = false,
  plotAppearance,
  fullscreen = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // FXC-9004: the `fullscreen` prop was never passed true (FullscreenButton
  // owned that state privately), so the chart stayed letterboxed at 420px in
  // fullscreen. Detect real fullscreen on this container too.
  const isFullscreen = useIsFullscreen(containerRef) || fullscreen;

  const data = useMemo(() => {
    const t = adaptiveSamplingTheme;
    const muted = plotAppearance?.muted ?? t.textMuted;
    const traces: Record<string, unknown>[] = [
      {
        type: 'heatmap',
        x,
        y,
        z,
        colorscale: paletteToColorscale(t.sequentialPalette),
        colorbar: {
          title: { text: zLabel, font: { color: muted, size: 11 } },
          tickfont: { color: muted, size: 10 },
          outlinewidth: 0,
          thickness: 10,
        },
        hovertemplate:
          `${xLabel}: %{x:.3g}<br>${yLabel}: %{y:.3g}<br>${zLabel}: %{z:.3g}<extra></extra>`,
      },
    ];

    if (uncertainty) {
      traces.push({
        type: 'contour',
        x,
        y,
        z: uncertainty,
        showscale: false,
        contours: { coloring: 'lines', showlabels: false },
        line: { color: plotAppearance?.grid ?? 'rgba(255,255,255,0.18)', width: 1 },
        hoverinfo: 'skip',
        opacity: 0.6,
      });
    }

    if (highlight === 'ridge') {
      const ridge = computeRidge(x, y, z);
      traces.push({
        type: 'scatter',
        mode: 'lines',
        x: ridge.x,
        y: ridge.y,
        line: { color: t.pink, width: 2.5, shape: 'spline' },
        showlegend: false,
        hoverinfo: 'skip',
        name: 'ridge',
      });
    }

    return traces;
  }, [x, y, z, uncertainty, xLabel, yLabel, zLabel, highlight, plotAppearance]);

  const layout = useMemo(() => {
    const a = plotAppearance;
    const axisOverride = a
      ? {
          gridcolor: a.grid,
          zerolinecolor: a.grid,
          linecolor: a.muted,
          color: a.muted,
          tickfont: { color: a.muted, size: 11 },
        }
      : {};
    return basePlotlyLayout({
      xaxis: {
        ...(basePlotlyLayout().xaxis as object),
        ...axisOverride,
        title: { text: xLabel },
      },
      yaxis: {
        ...(basePlotlyLayout().yaxis as object),
        ...axisOverride,
        title: { text: yLabel },
      },
      ...(a
        ? {
            paper_bgcolor: a.paperBg,
            plot_bgcolor: a.plotBg ?? a.paperBg,
            font: { family: 'Outfit, sans-serif', color: a.text, size: 12 },
            hoverlabel: {
              bgcolor: a.hoverBg ?? a.paperBg,
              bordercolor: a.hoverBorder ?? a.grid,
              font: { family: 'JetBrains Mono, monospace', color: a.text, size: 11 },
            },
          }
        : {}),
      autosize: true,
      height: isFullscreen || fillHeight ? undefined : 420,
    });
  }, [xLabel, yLabel, isFullscreen, fillHeight, plotAppearance]);

  const config = useMemo(
    () => ({
      displayModeBar: false,
      responsive: true,
    }),
    [],
  );

  return (
    <div
      ref={containerRef}
      className={`as-chart-wrap${isFullscreen ? ' as-fullscreen' : ''}`}
      style={fillHeight && !isFullscreen ? { height: '100%' } : undefined}
    >
      <div className="as-chart-fs-slot">
        <FullscreenButton targetRef={containerRef as React.RefObject<HTMLElement>} />
      </div>
      <Plot
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data={data as any}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        layout={layout as any}
        config={config}
        useResizeHandler
        style={{ width: '100%', height: '100%' }}
        onClick={(ev) => {
          if (!onClick) return;
          const p = ev?.points?.[0];
          if (p && typeof p.x === 'number' && typeof p.y === 'number') {
            onClick(p.x, p.y);
          }
        }}
        onHover={(ev) => {
          if (!onHover) return;
          const p = ev?.points?.[0];
          if (p && typeof p.x === 'number' && typeof p.y === 'number') {
            onHover({ x: p.x, y: p.y });
          }
        }}
        onUnhover={() => onHover?.(null)}
      />
      {overlay}
    </div>
  );
};

/**
 * Memoized export (FXC-9009 adversarial review): parents re-render at
 * store-patch rate; with array/handler props memoized upstream this stops
 * plotly receiving fresh props (and re-rendering) on unrelated ticks.
 */
export const HeatmapPanel = React.memo(HeatmapPanelImpl);
